import { createSelector } from '@reduxjs/toolkit';
import { RootState } from './index';
const normalizeTicker = (value: string | undefined | null): string =>
  (value ?? '').toString().trim().toUpperCase();

// Re-export stock selectors from dedicated files
export {
  selectStocks,
  selectStockByTicker,
  selectStocksEntities,
  selectStocksLoading,
  selectSortConfig,
  selectSortedStocks,
  selectFilteredStocks,
  selectFilteredAndSortedStocks,
  selectStockStatistics,
  selectStocksByPattern,
  selectPerformanceMetrics,
  selectStocksBySector,
  selectHighMomentumStocks,
  selectBreakoutCandidates
} from './selectors/stockSelectors';

export {
  selectScreeningStatus,
  selectScreeningResults,
  selectScreeningProgress,
  selectScreeningProgressPercentage,
  selectScreeningPassRate,
  selectIsScreeningActive,
  selectScreeningMetrics,
  selectScreeningPerformance
} from './selectors/screeningSelectors';

// Legacy selectors - maintained for backward compatibility

export const selectFavorites = (state: RootState) => state.favorites.favorites;
export const selectFavoritesLoading = (state: RootState) => state.favorites.loading;
export const selectFavoritesError = (state: RootState) => state.favorites.error;

export const selectFilters = (state: RootState) => state.filters;
export const selectActiveCategory = (state: RootState) => state.filters.active_category;
export const selectPatternWeights = (state: RootState) => state.filters.pattern_weights;

// Base screening and favorites selectors


export const selectChartData = (state: RootState) => state.chart.data;
export const selectChartLoading = (state: RootState) => state.chart.loading;
export const selectChartTicker = (state: RootState) => state.chart.ticker;

export const selectAIAnalysis = (state: RootState) => state.aiAnalysis;
export const selectAIAnalysisData = (state: RootState) => state.aiAnalysis.data;
export const selectAIAnalysisLoading = (state: RootState) => state.aiAnalysis.loading;

export const selectGlobalMessage = (state: RootState) => state.globalMessage;

export const selectDatabaseStats = (state: RootState) => state.database;

// Complex derived selectors
export const selectFavoriteByTicker = createSelector(
  [selectFavorites, (state: RootState, ticker: string) => ticker],
  (favorites, ticker) => {
    const target = normalizeTicker(ticker);
    return favorites.find((fav) => normalizeTicker(fav.ticker) === target);
  }
);

// Optimized favorites lookup with Map-based caching
export const selectFavoritesMap = createSelector(
  [selectFavorites],
  (favorites) => {
    const favoritesMap = new Map<string, boolean>();
    favorites.forEach((fav) => favoritesMap.set(normalizeTicker(fav.ticker), true));
    return favoritesMap;
  }
);

export const selectIsTickerFavorited = createSelector(
  [selectFavoritesMap, (state: RootState, ticker: string) => ticker],
  (favoritesMap, ticker) => favoritesMap.has(normalizeTicker(ticker))
);





export const selectFavoritesByColor = createSelector(
  [selectFavorites],
  (favorites) => {
    return favorites.reduce((acc, fav) => {
      if (!acc[fav.color]) {
        acc[fav.color] = [];
      }
      acc[fav.color]!.push(fav);
      return acc;
    }, {} as Record<string, typeof favorites>);
  }
);

export const selectStockAnalysisResult = createSelector(
  [selectAIAnalysisData, (state: RootState, ticker: string) => ticker],
  (analysisData, ticker) => {
    return analysisData?.results?.find(result => result.ticker === ticker);
  }
);

